<script setup>

import {computed, inject, onMounted, ref} from 'vue'
import API from "@/classes/global/API";

import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import StaticWidget from "@/components/common/other/StaticWidget";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import DateInput from "@/components/common/input/DateInput";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import SearchStockPillGroup from "@/components/warehouse/SearchStockPillGroup";
import GridCellDetail from "@/components/common/other/GridCellDetail";
import MoreInfoPopOutPanel from "@/components/common/other/MoreInfoPopOutPanel";
import StockDescriptions from "@/components/warehouse/StockDescriptions";
import Modal from "@/components/common/other/Modal.vue";
import AddressEntry from "@/components/common/other/AddressEntry.vue";
import ContactDetail from "@/classes/bookings/ContactDetail";
import ContactEntry from "@/components/common/other/ContactEntry.vue";
import Address from "@/classes/global/Address";
import PickStockConsignee from "@/classes/stock/PickStockConsignee";
// temp
import ValidatedField from "@/classes/global/ValidatedField";

import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useMapAddressAndContacts} from "@/composables/common/useMapAddressAndContacts";
import {useClearDescriptionHeaders} from "@/composables/stock/useClearDescriptionHeaders";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";
import {useTimeGenerator} from "@/composables/common/useTimeGenerator";
import Notification from "@/classes/component-classes/Notification";
import {useValidateRequiredFields} from "@/composables/common/useValidateRequiredFields";
import {PickStockValidation} from "@/classes/stock/PickStockValidation";
import CheckBox from "@/components/common/input/CheckBox.vue";

const store = useStore();
const route = useRoute();
const pickStock = inject("pickStock");

const validationViewModel = inject("validationViewModel");

const props = defineProps({

  modelValue: {}
})

const emits = defineEmits(['update-picked-items']);

const address = computed(() => {

  if (pickStock.value.openEditConsigneeModal) {

    pickStock.value.newConsigneeCompany = JSON.parse(JSON.stringify(pickStock.value.consignee));

  }

  return pickStock.value.newConsigneeCompany;
})

const contact = computed(() => {

  if (pickStock.value.openAddContactModal) return pickStock.value.newConsigneeContact;

  return pickStock.value.consignee.selectedContact;
})

onMounted(() => {

  prepareMetaData();

})

async function prepareMetaData() {

  const response = await API.getStockMetaData(route, 'pickStockValidationFields');

  if (response) pickStock.value.metaData = response;

  //TODO Remove when server ready
  pickStock.value.metaData.validatedFields.push(

      new ValidatedField('name', true, true, 'Company Name'),
      new ValidatedField('addressLine1', true, true, 'Address Line 1'),

      new ValidatedField('firstName', true, true, 'First Name'),
      new ValidatedField('lastName', true, true, 'Last Name')
  )

}

async function getCompanyDetails(name) {

  pickStock.value.consignee.name = name.originValue;

  let response = await API.getCompanyDetail(name.id, route)

  if (response) useMapAddressAndContacts(response, pickStock.value);

}

function handlePick(rowData) {

  emits('update-picked-items', rowData.pickQuantity)

  if (rowData.pickQuantity) {

    let isExistingRowData = props.modelValue.pickedItems.find((item) => {
      return rowData.skuComposite === item.skuComposite
    })

    if (isExistingRowData) return;

    props.modelValue.pickedItems.push(rowData);
    props.modelValue.openPicksDrawer = true;
    rowData.allocated = rowData.pickQuantity;
  }
}

function toggleSearchGroup(button) {

  if (button && pickStock.value.searchGroup) return;
  if (!button && !pickStock.value.searchGroup) return;

  pickStock.value.searchGroup = !pickStock.value.searchGroup;
}

async function getStockHeaders(selected) {

  useClearDescriptionHeaders(pickStock.value);

  const response = await API.getStockDescriptions(route, selected.id);

  if (response) {

    for (const each in response) {

      if (response[each]) {

        pickStock.value.skuGroupSelected = true;
        pickStock.value.searchPills[each].enabled = true;
        pickStock.value.searchPills[each].label = response[each];

      } else {

        pickStock.value.searchPills[each].enabled = false;

      }
    }
  }

  handlePickStockSearch();

}

async function clearHeaders() {

  useClearDescriptionHeaders(pickStock.value);

  if (useCheckSearchCriteriaExists(props.modelValue.searchCriteria)) {

    handlePickStockSearch();

  } else {

    pickStock.value.searchData = [];
  }

}

async function handlePickStockSearch() {

  const response = await API.searchStock(route, pickStock.value.searchCriteria, 'pickstock');

  pickStock.value.searchData = response;

}

function mapSelectedConsigneeContactDetail(contact) {

  pickStock.value.consignee.selectedContact = contact;
}

function handleClearCompany() {

  pickStock.value.consignee = new PickStockConsignee();
}

function disableGridShowButton(skuProperties) {

  for (const each in skuProperties) {
    if (skuProperties[each].value) return true
  }

  return false
}

function handleClearData() {

  pickStock.value.searchData = [];
}

function addNewConsigneeCompany() {

  pickStock.value.newConsigneeCompany = new Address();
  pickStock.value.openNewConsigneeModal = true;
}

function editConsigneeCompany() {

  pickStock.value.confirmUpdateConsignee = false;
  pickStock.value.consignee.name = pickStock.value.consignee.name.split('-')[0].trim();
  pickStock.value.consigneeCopy = pickStock.value.consignee;

  pickStock.value.openEditConsigneeModal = true;
}

function prepareConsigneeSave() {

  if(useValidateRequiredFields(pickStock.value.newConsigneeCompany, validationViewModel.value.pickStock, pickStock.value.metaData.validatedFields)) {

    pickStock.value.confirmSaveConsignee = true;
  }

}

function prepareConsigneeUpdate() {

  if(useValidateRequiredFields(pickStock.value.newConsigneeCompany, validationViewModel.value.pickStock, pickStock.value.metaData.validatedFields)) {

    pickStock.value.confirmUpdateConsignee = true;
  }

}

async function saveNewConsigneeCompany() {

    pickStock.value.newConsigneeCompany.newCompanySaved = true;

    const addressDetail = await saveCompanyDetail(pickStock.value.newConsigneeCompany);

    if(addressDetail) {

      pickStock.value.newConsigneeCompany.id = addressDetail.id;
      pickStock.value.confirmSaveConsignee = false;
    }

}

async function saveCompanyDetail(company, isEdit) {

  company.country = company.country.split('-')[0].trim();

  // This just satisfies re-use of the API
  company.isShipperAddress = false;

  const addressDetail = await API.saveAddress('consignee', company);

  if(addressDetail) {

    store.state.banners.success = new Notification(true, 'The Data Was Successfully Saved');

    if(isEdit) {

      pickStock.value.openEditConsigneeModal = false;
      mapEditedConsignee(company);
    }
  }

  return addressDetail;
}

function mapEditedConsignee(company) {

  pickStock.value.consignee.name = company.name;
  pickStock.value.consignee.addressLine1 = company.addressLine1;
  pickStock.value.consignee.addressLine2 = company.addressLine2;
  pickStock.value.consignee.city = company.city;
  pickStock.value.consignee.county = company.county;
  pickStock.value.consignee.postCode = company.postCode;
  pickStock.value.consignee.country = company.country;
  pickStock.value.vatNumber = company.vatNumber;
}


function cancelConsigneeSave(isEdit) {

  if(isEdit) return pickStock.value.confirmUpdateConsignee = false;

  pickStock.value.confirmSaveConsignee = false;
}

async function saveEditedConsigneeCompany(company) {

  if(!pickStock.value.keepExistingContacts) company.contacts = [];

  pickStock.value.confirmUpdateConsignee = false;
  saveCompanyDetail(company, true);
}

async function saveConsigneeContact(contact) {

  if(useValidateRequiredFields(contact, validationViewModel.value.pickStock, pickStock.value.metaData.validatedFields)) {

    contact.value = contact.title + ' ' + contact.firstName + ' ' + contact.lastName;

    if(contact.id === 0) {

      pickStock.value.consignee.contacts.push(contact);

    } else {

      let existingContact = pickStock.value.consignee.contacts.find(x => x.id === contact.id);

      mapEditedContact(contact, existingContact);
    }

    const addressDetail = await saveCompanyDetail(pickStock.value.consignee);

    if(addressDetail) {

      contact.id = addressDetail.contacts[addressDetail.contacts.length -1].id;
      pickStock.value.newConsigneeContact.newContactSaved = true;
    }

  }

}

function mapEditedContact(newContactData, existingContactData) {

  existingContactData.id = newContactData.id;
  existingContactData.title = newContactData.title;
  existingContactData.firstName = newContactData.firstName;
  existingContactData.lastName = newContactData.lastName;
  existingContactData.department = newContactData.lastName;
  existingContactData.phone = newContactData.phone;
  existingContactData.email = newContactData.email;
  existingContactData.value = newContactData.value;
}

function editNewCompany() {

  pickStock.value.newConsigneeCompany.newCompanySaved = false;
}

function clearConsigneeContact() {

  pickStock.value.consignee.selectedContact = new ContactDetail();
}

function addNewConsigneeContact() {

  pickStock.value.newConsigneeContact = new ContactDetail();
  pickStock.value.openAddContactModal = true;
}

function editConsigneeContact() {

  pickStock.value.openEditContactModal = true;
}

function editNewContact() {

  pickStock.value.newConsigneeContact.newContactSaved = false;
}

function clearNewContactForm() {

  pickStock.value.newConsigneeContact = new ContactDetail();
}

function clearNewConsigneeForm() {

  pickStock.value.newConsigneeCompany = new Address();
}

function handleCloseModal() {

  validationViewModel.value.pickStock = new PickStockValidation();

  clearNewConsigneeForm();
}

function useUnSavedAddress(isEdit) {

  if(useValidateRequiredFields(pickStock.value.newConsigneeCompany, validationViewModel.value.pickStock, pickStock.value.metaData.validatedFields)) {

    const addressFields = ['name', 'addressLine1', 'addressLine2', 'city', 'postCode', 'county', 'country', 'vatNumber'];

    addressFields.forEach(field => {

      if (pickStock.value.newConsigneeCompany[field] !== pickStock.value.consigneeCopy[field]) {

        return pickStock.value.newConsigneeCompany.id = 0;
      }

    })

    pickStock.value.consignee = pickStock.value.newConsigneeCompany;

    if (isEdit) return pickStock.value.openEditConsigneeModal = false;

    pickStock.value.openNewConsigneeModal = false;
  }
}

function useUnSavedContact(contact) {

  if(useValidateRequiredFields(contact, validationViewModel.value.pickStock, pickStock.value.metaData.validatedFields)) {

    pickStock.value.consignee.selectedContact = pickStock.value.newConsigneeContact;

    pickStock.value.consignee.selectedContact.value = pickStock.value.newConsigneeContact.title + ' '
        +  pickStock.value.newConsigneeContact.firstName + ' ' + pickStock.value.newConsigneeContact.lastName;

    pickStock.value.openAddContactModal = false;
    pickStock.value.openEditContactModal = false;

  }

}

</script>

<template class="cmp-pick-stock">
  <StaticWidget title="Delivery Information" height="250" background="#fcfcfc">
    <template #extra-content>
      <div style="margin-left: 80px; font-weight: normal">Warehouse Job Number:
        <span style="color: var(--aqua-blue-definition)">{{ pickStock.jobNumber }}</span>
        <Button
            style="margin-left: 20px;"
            type="toolbar"
            text="Cancel Job">
        </Button>
      </div>
    </template>
    <template #right-side>
      <Button
          type="warning"
          @click="props.modelValue.openDocsDrawer = true;"
          icon="<i class='fa-solid fa-folder-plus'></i>"
          text="Add/View Docs">
      </Button>
      <Button
          type="primary"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save & Submit">
      </Button>
    </template>
    <div class="cmp-pick-stock_delivery-section-wrapper">
      <div class="cmp-pick-stock_delivery-section-layout">
        <div class="cmp-pick-stock_address-entry-section">
          <div class="cmp-pick-stock_field-button-layout">
            <MultiSearchDropDown
                :key="pickStock.metaData.consignees"
                :allowCustomValue="false"
                :useServerSearch="true"
                :maxLength="50"
                label="Consignee & Address"
                :label-top="true"
                :width="400"
                searchType="consignee"
                v-model="pickStock.consignee.name"
                @select="getCompanyDetails($event, 'consignee')"
                @clear="handleClearCompany()"
                :dataSource="pickStock.metaData.consignees"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
            <div class="cmp-pick-stock_address-buttons">
              <i @click="addNewConsigneeCompany" class="fa-solid fa-circle-plus global-add-icon" title="Create New Consignee"
                 style="margin-right: 8px;"></i>
              <i v-if="pickStock.consignee.name" @click="editConsigneeCompany"
                 class="fa-solid fa-pen-to-square global-edit-icon" title="Edit This Consignee">
              </i>
            </div>
          </div>
          <div class="cmp-pick-stock_address-container">
            <div class="cmp-pick-stock_address-details">
              <div title="Address Line 1">{{ pickStock.consignee.addressLine1 }}</div>
              <div title="Address Line 2">{{ pickStock.consignee.addressLine2 }}</div>
              <div title="City">{{ pickStock.consignee.city }}</div>
              <div title="County">{{ pickStock.consignee.county }}</div>
              <div title="Post Code">{{ pickStock.consignee.postCode }}</div>
              <div title="Country">{{ pickStock.consignee.country }}</div>
            </div>
          </div>
          <div style="position: absolute; top: 140px">
            <div class="cmp-pick-stock_field-button-layout">
              <MultiSearchDropDown
                  :key="pickStock.consignee.contacts"
                  :width="400"
                  :maxLength="50"
                  label="Contact"
                  style="margin-top: 20px;"
                  :label-top="true"
                  placeholder="Select..."
                  :allowCustomValue="true"
                  @select="mapSelectedConsigneeContactDetail"
                  @clear="clearConsigneeContact"
                  :disableFilter="true"
                  v-model="pickStock.consignee.selectedContact.value"
                  :data-source="pickStock.consignee.contacts"
                  :selectPrimitiveValue="true">
              </MultiSearchDropDown>
              <div style="margin-top: 20px;">
                <i v-if="pickStock.consignee.name"
                   @click="addNewConsigneeContact()"
                   class="fa-solid fa-circle-plus global-add-icon"
                   style="margin-right: 8px">
                </i>
                <i v-if="pickStock.consignee.selectedContact.value"
                   @click="editConsigneeContact()"
                   class="fa-solid fa-pen-to-square global-edit-icon">
                </i>
              </div>
            </div>
            <div class="cmp-pick-stock_contact-container">
              <div class="cmp-pick-stock_contact-details">
                <div class="cmp-pick-stock_contact-info">
                  <div v-if="pickStock.consignee.selectedContact.phone">
                    <i  class="fa-duotone fa-square-phone cmp-pick-stock_contact-icons"></i>
                    Tel: {{ pickStock.consignee.selectedContact.phone }}
                  </div>
                  <div v-if="pickStock.consignee.selectedContact.email">
                    <i class="fa-duotone fa-envelope cmp-pick-stock_contact-icons"></i>
                    Email: {{ pickStock.consignee.selectedContact.email }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="cmp-pick-stock_delivery-information-area">
          <TextInput
              v-model="pickStock.customerRef"
              :width="400"
              :maxLength="50"
              :labelTop="true"
              label="Customer Ref">
          </TextInput>
          <div class="cmp-pick-stock_release-date-area">
            <DateInput
                v-model="pickStock.releaseDate"
                style="margin-top: 20px"
                :labelWidth="118"
                :labelTop="true"
                label="Release Date">
              <el-date-picker
                  :clearable="false"
                  value-format="DD-MMM-YY"
                  v-model="pickStock.releaseDate"></el-date-picker>
            </DateInput>
            <MultiSearchDropDown
                v-model="pickStock.releaseTime"
                style="margin-top: 20px"
                :width="121"
                :labelWidth="75"
                :labelTop="true"
                :maxLength="50"
                placeholder="Select..."
                :allowCustomValue="false"
                :disableFilter="true"
                label="Release Time"
                :data-source="useTimeGenerator(24)">
              :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
          <DateInput
              v-model="pickStock.deliveryDate"
              style="margin-top: 20px"
              :labelWidth="118"
              :labelTop="true"
              label="Delivery Date">
            <el-date-picker
                :clearable="false"
                value-format="DD-MMM-YY"
                v-model="pickStock.deliveryDate"></el-date-picker>
          </DateInput>
          <TextInput
              v-model="pickStock.action"
              :width="400"
              style="margin-top: 20px"
              :labelTop="true"
              :maxLength="50"
              label="Action">
          </TextInput>
          <DropDownTextArea
              v-model="pickStock.deliveryInstructions"
              style="margin-top: 20px"
              :labelTop="true"
              :width="400"
              label="Delivery Instructions">
          </DropDownTextArea>
        </div>
      </div>
    </div>
  </StaticWidget>
  <StaticWidget title="Search Stock" height="410">
    <template #extra-content>
      <div style=" margin-left: 80px; font-weight: normal">SKU Group</div>
      <div style="font-weight: normal" class="global-flex-layout">
        <MultiSearchDropDown
            :key="pickStock.metaData.skuGroups"
            :width="320"
            :labelWidth="85"
            :maxLength="50"
            placeholder="Select..."
            @select="getStockHeaders"
            @clear="clearHeaders"
            :allowCustomValue="false"
            :disableFilter="true"
            v-model="pickStock.searchCriteria.skuGroup"
            :dataSource="pickStock.metaData.skuGroups"
            :selectPrimitiveValue="true">
        </MultiSearchDropDown>
        <StaticTooltip :icon="false">
          <template #content>
            <div style="width: 340px;">
              <strong>Search</strong> by completing any field and press any search icon
              <span><i class="fa-duotone fa-square-arrow-right" style="color: #114362"></i></span> or press enter within
              the field.
              Any fields with data are considered when searching records, some criteria may return no results. Selecting
              a
              SKU Group will bring new search criteria where available. A search group toggle will appear to view other
              fields.
              Press Refresh to return the latest records for the current search, or reset the data to initial records.
            </div>
          </template>
          <i :class="store.state.globalHelpIcon"></i>
        </StaticTooltip>
        <div v-if="pickStock.skuGroupSelected" class="cmp-stock_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="1"
              :class="{'cmp-stock_search-group-selection-icon-active' : pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="2"
              :class="{'cmp-stock_search-group-selection-icon-active' : !pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
        </div>
      </div>
    </template>
    <template #right-side>
      <Button
          @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
          icon="<i class='fa-duotone fa-grid-2'></i>"
          type="info"
          :badgeData="pickStock.pickedItems.length"
          text="View Picks">
      </Button>
    </template>
    <SearchStockPillGroup
        url="pickstock"
        @clear="handleClearData"
        v-model="pickStock">
    </SearchStockPillGroup>
    <div style="margin-top: 10px;">
      <StickyGrid
          :key="pickStock.searchData.length"
          :allowGridMenu="false"
          :height="280"
          :width="98"
          :page-size="200"
          :selectable="false"
          :highlightRows="false"
          :allow-column-resize="false"
          :row-selection="false"
          v-model:currentPage="pickStock.searchResultCurrentPage"
          v-model:currentPaginationRange="pickStock.searchResultPaginationRange"
          :dataSource="pickStock.searchData"
          :columns="pickStock.searchGridColumns">
        <!--        <template v-slot:available="row">-->
        <!--          <div style="display: grid; grid-template-columns: 40px 100px;">-->
        <!--            {{row.rowData.available}}<span v-if="row.rowData.isLowStock" style="font-size: 11px; color: #b04545">-->
        <!--            <i class="fa-solid fa-triangle-exclamation" style="padding-top: 6px"></i> Low Stock!</span>-->
        <!--          </div>-->
        <!--        </template>-->
        <template v-slot:pick="row">
          <Button
              :disabled="row.rowData.isZeroQuantity"
              @click="handlePick(row.rowData)"
              icon="<i class='fa-duotone fa-circle-plus'></i>"
              type="success"
              text="Pick">
          </Button>
        </template>
        <template #menu-slot-items v-if="pickStock.searchData.length > 0">
          <Button
              @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
              icon="<i class='fa-duotone fa-grid-2'></i>"
              type="toolbar"
              :badgeData="pickStock.pickedItems.length"
              text="View Picks">
          </Button>
          <Button
              @click="handlePick(row.rowData)"
              type="toolbar"
              text="Pick All">
          </Button>
          <!--          <div>Stock Over Pick Available-->
          <!--            <i class="fa-solid fa-circle-check" style="color: #3f9968;"></i>-->
          <!--          </div>-->
        </template>
        <template v-slot:pickQuantity="row">
          <TextInput
              :disable="row.rowData.isZeroQuantity"
              v-model="row.rowData.pickQuantity"
              :width="60"
              :maxLength="8">
          </TextInput>
        </template>
        <template v-slot:comments="row">
          <DropDownTextArea
              v-model="row.rowData.comments"
              :width="310">
          </DropDownTextArea>
        </template>
        <template v-slot:skuProperties="row">
          <GridCellDetail :clickToView="true" v-if="disableGridShowButton(row.grid.skuProperties)">
            <template #details>
              <MoreInfoPopOutPanel
                  v-model="row.grid">
                <template #header>Stock Item Properties</template>
                <template #header-extra>SKU Number:
                  <span style="color: var(--aqua-blue-definition)"> {{ row.grid.skuNumber }}</span></template>
                <template #content>
                  <StockDescriptions v-model="row.grid.skuProperties"></StockDescriptions>
                </template>
              </MoreInfoPopOutPanel>
            </template>
            <template #cell-data>
              <Button
                  v-if="disableGridShowButton(row.grid.skuProperties)"
                  type="toolbar"
                  text="View">
              </Button>
            </template>
          </GridCellDetail>
        </template>
      </StickyGrid>
    </div>
  </StaticWidget>
  <Modal
      v-model="pickStock.openNewConsigneeModal"
      :showClose="false"
      @close="handleCloseModal()"
      title="New Consignee"
      width="480px"
      height="380px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            v-if="!pickStock.confirmSaveConsignee"
            validation-model="pickStock"
            :countries="pickStock.metaData.countries"
            v-model:requiredFields="pickStock.metaData.validatedFields"
            v-model="address">
          <template #vat-number-flex-content v-if="pickStock.newConsigneeCompany.name && pickStock.newConsigneeCompany.addressLine1">
            <div class="cmp-pick-stock_address-link"><span @click="useUnSavedAddress(false)">Use this address</span>
              <i title="Use the address only for this job. It will not be saved. If you save later a NEW consignee will
be added to your database." class="fa fa-info-circle icon-info"/>
            </div>
          </template>
        </AddressEntry>
        <div v-else class="cmp-pick-stock_address-list">
         <div style="font-weight: bold">{{pickStock.newConsigneeCompany.name}}</div>
          <div style="font-weight: normal">
            <div>{{pickStock.newConsigneeCompany.addressLine1}}</div>
            <div>{{pickStock.newConsigneeCompany.addressLine2}}</div>
            <div>{{pickStock.newConsigneeCompany.city}}</div>
            <div>{{pickStock.newConsigneeCompany.county}}</div>
            <div>{{pickStock.newConsigneeCompany.postCode}}</div>
            <div>{{pickStock.newConsigneeCompany.country}}</div>
          </div>
          <div class="cmp-pick-stock_confirm-save-address">
            <span v-if="!pickStock.newConsigneeCompany.newCompanySaved">Update Consignee?</span>
            <span v-else>Create New Consignee?</span>
            <Button
                style="margin-left: 20px"
                @click="saveNewConsigneeCompany"
                text="Yes"
                type="success">
            </Button>
            <Button
                @click="cancelConsigneeSave"
                text="No"
                type="secondary">
            </Button>
          </div>
        </div>
      </div>
    </template>
    <template #footer v-if="!pickStock.confirmSaveConsignee">
      <Button
          v-if="pickStock.newConsigneeCompany.newCompanySaved"
          @click="editNewCompany"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Edit"
          type="info">
      </Button>
      <Button
          v-if="pickStock.newConsigneeCompany.newCompanySaved"
          @click="clearNewConsigneeForm"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Add New"
          type="secondary">
      </Button>
      <Button
          v-if="!pickStock.newConsigneeCompany.newCompanySaved"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="prepareConsigneeSave"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openEditConsigneeModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Edit Consignee"
      width="480px"
      height="400px">
    <template #icon><i class="fa-solid fa-pen-to-square global-edit-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            v-if="!pickStock.confirmUpdateConsignee"
            validation-model="pickStock"
            v-model:requiredFields="pickStock.metaData.validatedFields"
            :countries="pickStock.metaData.countries"
            v-model="address">
          <template #vat-number-flex-content>
            <div class="cmp-pick-stock_address-link"><span @click="useUnSavedAddress(true)">Use this address</span>
              <i title="Use the address only for this job. It will not save any changes for this consignee.
If you make changes and save after using the address, a NEW consignee will be added to your database." class="fa fa-info-circle icon-info"/>
            </div>
          </template>
          <template #keep-contacts>
            <CheckBox
                v-model="pickStock.keepExistingContacts"
                label="keep existing contacts">
            </CheckBox>
          </template>
        </AddressEntry>
        <div v-else class="cmp-pick-stock_address-list">
          <div style="display: flex; gap:160px">
            <div>
              <div style="font-weight: bold">{{pickStock.newConsigneeCompany.name}}</div>
              <div style="font-weight: normal">
                <div>{{pickStock.newConsigneeCompany.addressLine1}}</div>
                <div>{{pickStock.newConsigneeCompany.addressLine2}}</div>
                <div>{{pickStock.newConsigneeCompany.city}}</div>
                <div>{{pickStock.newConsigneeCompany.county}}</div>
                <div>{{pickStock.newConsigneeCompany.postCode}}</div>
                <div>{{pickStock.newConsigneeCompany.country}}</div>
              </div>
            </div>
            <div style="font-size: 110px; opacity: 20%; color: #0d7979"><i class="fa-duotone fa-location-dot"></i> </div>
          </div>
          <div class="cmp-pick-stock_confirm-save-address">
            <span v-if="pickStock.newConsigneeCompany.id !== 0">Update Consignee?</span>
            <span v-else>Create New Consignee?</span>
            <Button
                style="margin-left: 20px"
                @click="saveEditedConsigneeCompany(address)"
                text="Yes"
                type="success">
            </Button>
            <Button
                @click="cancelConsigneeSave(true)"
                text="No"
                type="secondary">
            </Button>
          </div>
          <span style="font-size: 11px" v-if="pickStock.newConsigneeCompany.id === 0">
            This was previously used as a one time address. Saving will create a new contact!
          </span>
        </div>
      </div>
    </template>
    <template #footer>
      <Button
          v-if="!pickStock.confirmUpdateConsignee"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="prepareConsigneeUpdate()"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openAddContactModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Add Contact"
      width="350px"
      height="280px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            @use-contact="useUnSavedContact(contact)"
            validation-model="pickStock"
            v-model:requiredFields="pickStock.metaData.validatedFields"
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer v-if="pickStock.consignee.id !== 0">
      <Button
          v-if="pickStock.newConsigneeContact.newContactSaved"
          @click="editNewContact"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Edit"
          type="info">
      </Button>
      <Button
          v-if="pickStock.newConsigneeContact.newContactSaved"
          @click="clearNewContactForm"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Add New"
          type="secondary">
      </Button>
      <Button
          v-if="!pickStock.newConsigneeContact.newContactSaved"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="saveConsigneeContact(contact)"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openEditContactModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Edit Contact"
      width="350px"
      height="280px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            @use-contact="useUnSavedContact(contact)"
            validation-model="pickStock"
            v-model:requiredFields="pickStock.metaData.validatedFields"
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer>
      <Button
          v-if="pickStock.consignee.id !== 0"
          @click="saveConsigneeContact(contact)"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save"></Button>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>

.cmp-pick-stock {

  &_address-entry {
    margin-top: 30px;
  }

  &_header-text {
    font-size: 15px;
    //font-weight: bold;
    color: #383838;
  }

  &_delivery-section-layout {
    margin-top: 5px;
    padding: 10px 0 0 20px;
    display: grid;
    gap: 170px;
    grid-template-columns: 460px 40%;
  }

  &_delivery-section-wrapper {
    height: 400px;
  }

  &_address-save-button {
    padding: 5px 0 0 88px;
  }

  &_field-button-layout {
    display: flex;
    gap: 14px;
  }

  &_address-entry-section {
    position: relative;
  }

  &_delivery-information-area {
    padding-left: 70px;
  }

  &_address-details {
    font-size: 13px;
    color: #343434;
    padding: 3px;
  }

  &_contact-info {
    margin-top: 2px;
    font-size: 13px;
    padding-left: 3px;
  }

  &_contact-icons {
    font-size: 13px;
    color: #0e9182;
  }

  &_release-date-area {
    display: flex;
    gap: 170px;
    width: 400px;
    height: 50px;
  }

  &_address-container, &_contact-container {
    margin-top: 6px;
    padding: 3px;
    height: 100px;
    width: 392px;
    background: #ffffff;
    box-shadow: 0px 1px 4px 0px #cfcfcf;
  }

  &_contact-container {
    height: 40px;
  }

  &_address-link {
    font-size: 12px;
    color: #0fa4c7;
  }

  &_address-link:hover {
    text-decoration: underline;
  }

  &_address-list {
    font-size: 14px;
  }

  &_confirm-save-address {
    margin-top: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 12px;
    height: 80px;
    //background: #fdf7f7;
    border-radius: 2px;
    font-weight: bold;
    color: #982222;
  }

}

</style>
